import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { cardsResponseSchema, type CardsResponse } from "@/api/cards";
import { ApiError, apiGet, apiPost } from "@/api/client";
import { kpiSnapshotSchema, type KpiSnapshot } from "@/api/kpi";

/**
 * ikas gomulu oturum + hafif ozet veri cagrilari — docs/faz3-ikas-spec.md §6,
 * docs/faz5-arayuz-spec.md §2. `src/api/*.ts` PARALEL paketlerin dosyasi
 * (dokunulmaz) — bu yuzden `/cards` ve `/kpi/snapshot` icin PAYLASILAN
 * semalar (`cardsResponseSchema`/`kpiSnapshotSchema`) ICE AKTARILIR, kendi
 * `useQuery` sarmalayicilarimiz burada tanimlanir (gomulu oturum hazir
 * olmadan `enabled:false`, 401'de otomatik /giris yonlendirmesi YOK —
 * `noRedirect:true`, gomulu sayfa kendi 401/hata durumunu gosterir).
 */

const embeddedSessionResponseSchema = z.object({ token: z.string() });

export function fetchEmbeddedSession(storeName: string): Promise<string> {
  return apiPost(
    "/api/v2/ikas/embedded-session",
    embeddedSessionResponseSchema,
    { storeName },
    { noRedirect: true },
  ).then((res) => res.token);
}

export function useEmbeddedSessionMutation() {
  return useMutation({ mutationFn: (storeName: string) => fetchEmbeddedSession(storeName) });
}

export const EMBED_CARDS_QUERY_KEY = ["embed", "cards"] as const;

/** `enabled`: bearer token ayarlanana kadar cagrilmaz (401'i tetiklememek icin). */
export function useEmbedCardsQuery(enabled: boolean) {
  return useQuery<CardsResponse>({
    queryKey: EMBED_CARDS_QUERY_KEY,
    queryFn: () => apiGet("/api/v2/cards", cardsResponseSchema, { noRedirect: true }),
    enabled,
    retry: false,
  });
}

export const EMBED_KPI_SNAPSHOT_QUERY_KEY = ["embed", "kpi", "snapshot"] as const;

/** kpi.ts'teki useKpiSnapshot ile ayni 404→null davranisi (snapshot henuz yoksa). */
export function useEmbedKpiSnapshotQuery(enabled: boolean) {
  return useQuery<KpiSnapshot | null>({
    queryKey: EMBED_KPI_SNAPSHOT_QUERY_KEY,
    queryFn: async () => {
      try {
        return await apiGet("/api/v2/kpi/snapshot", kpiSnapshotSchema, { noRedirect: true });
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled,
    retry: false,
  });
}
